import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap, takeUntil } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientSatisfactionSurveyService {
  private apiUrl = 'api/patient-satisfaction-surveys';
  private patientSurveysSubject = new BehaviorSubject<any[] | null>(null);
  private editingSurveySubject = new BehaviorSubject<any>(null);

  patientSurveys$ = this.patientSurveysSubject.asObservable();
  editingSurvey$ = this.editingSurveySubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientSurveysSubject.next([]);
          return this.loadSurveysByPatient(patient.id);
        } else {
          this.patientSurveysSubject.next([]);
          return [];
        }
      }),
      tap((results: any) => {
        const surveys = results ? results.data : [];
        this.patientSurveysSubject.next(surveys);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    let currentPatientId: string | undefined = undefined;

    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : undefined;
      this.patientId = currentPatientId;
    });

    // Listener for when a new survey is created
    this.socketService.listen('surveyCreated', 'patient-satisfaction-survey-service').subscribe(survey => {
      if (survey.patientId === currentPatientId) {
        const currentSurveys = this.patientSurveysSubject.getValue() || [];
        currentSurveys.unshift(survey);
        this.patientSurveysSubject.next(currentSurveys);
      }
    });

    // Listener for when a survey is completed
    this.socketService.listen('surveyCompleted', 'patient-satisfaction-survey-service').subscribe(completedSurvey => {
      if (completedSurvey.patientId === currentPatientId) {
        const currentSurveys = this.patientSurveysSubject.getValue() || [];
        const updatedSurveys = currentSurveys.map(survey => 
          survey.id === completedSurvey.id ? completedSurvey : survey
        );
        this.patientSurveysSubject.next(updatedSurveys);
      }
    });

    // Listener for when a survey is deleted
    this.socketService.listen('surveyDeleted', 'patient-satisfaction-survey-service').subscribe(deletedSurvey => {
      if (deletedSurvey.patientId === currentPatientId) {
        const currentSurveys = this.patientSurveysSubject.getValue() || [];
        const updatedSurveys = currentSurveys.filter(survey => survey.id !== deletedSurvey.id);
        this.patientSurveysSubject.next(updatedSurveys);
      }
    });
  }

  loadSurveysByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  validateSurveyToken(token: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/validate/${token}`);
  }

  getSurveyById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  submitSurveyByToken(token: string, responses: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/submit-by-token`, { token, responses });
  }

  
  submitSurvey(id: string, responses: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/${id}/submit`, { responses }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  createSurvey(data: { groupId: string, patientId: string, appointmentId: string, surveyTemplateId: string }): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  createManualSurvey(groupId: string, patientId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}`, {
      groupId,
      patientId,
      appointmentId: null,
      surveyTemplateId: null
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getSurveysByGroup(groupId: string, params?: any): Observable<any> {
    const token = localStorage.getItem('token');
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.http.get<any>(`${this.apiUrl}/group/${groupId}${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteSurvey(surveyId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete<any>(`${this.apiUrl}/${surveyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingSurvey(survey: any | null) {
    this.editingSurveySubject.next(survey);
  }
} 